/**
 * AssistPanel — the ⌘I "compila con AI" side panel for the console.
 *
 * A persistent right-hand panel (NOT a modal) opened by ⌘I. It is the
 * console's natural-language assistant: a prompt box that calls the EXISTING
 * console agentic endpoint via ``runConsoleAgenticAnswer`` (with
 * ``createConsoleConversation`` folded in for multi-turn memory) — the SAME
 * client functions the Playground uses. No new endpoint. It renders the
 * answer plus the ordered ``steps[]`` audit trail, and its eyebrow is
 * context-aware (the section you opened it from).
 *
 * Rendered INSIDE the console root (by ``ConsoleCommandCenter``), so it
 * inherits the ``.console-theme`` scope directly — no portal, no re-stamp.
 */

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation } from "@tanstack/react-query";
import { CornerDownLeft, Sparkles, X } from "lucide-react";
import * as React from "react";

import { cn } from "@ando/ui";

import {
  createConsoleConversation,
  getConsoleErrorMessage,
  runConsoleAgenticAnswer,
} from "../api/client";
import type { ConsoleAgenticRequest, ConsoleAgenticResponse } from "../api/types";
import { ConBadge, ConButton, ConKbd, ConTextarea, conFocusRing } from "./atoms";

interface AssistTurn {
  id: string;
  question: string;
  response: ConsoleAgenticResponse | null;
  error: string | null;
}

function StepStatusBadge({ status }: { status: string }): React.ReactElement {
  const tone = status === "ok" ? "success" : status === "error" ? "danger" : "warning";
  return (
    <ConBadge tone={tone}>
      <span className="font-mono">{status}</span>
    </ConBadge>
  );
}

function TurnView({ turn }: { turn: AssistTurn }): React.ReactElement {
  const { i18n } = useLingui();
  const res = turn.response;
  return (
    <div className="flex flex-col gap-2">
      {/* The question — a quiet right-aligned bubble outline. */}
      <p className="self-end rounded-md rounded-br-sm border border-[color:var(--con-border)] bg-[color:var(--con-surface-sunken)] px-2.5 py-1.5 text-[length:var(--con-text-xs)] text-[color:var(--con-on-surface)]">
        {turn.question}
      </p>

      {turn.error ? (
        <p
          role="alert"
          className="rounded-sm border border-[color:var(--con-danger)] bg-[color:var(--con-danger-soft)] px-2.5 py-1.5 text-[length:var(--con-text-xs)] text-[color:var(--con-danger-text)]"
        >
          {turn.error}
        </p>
      ) : res ? (
        <div className="flex flex-col gap-2">
          {res.no_results || res.answer == null ? (
            <p className="text-[length:var(--con-text-xs)] text-[color:var(--con-on-surface-faint)] italic">
              <Trans>Nessun risultato su cui fondare una risposta.</Trans>
            </p>
          ) : (
            <p className="whitespace-pre-wrap text-[length:var(--con-text-sm)] text-[color:var(--con-on-surface)] leading-relaxed">
              {res.answer}
            </p>
          )}

          {(res.steps ?? []).length > 0 ? (
            <details className="group rounded-sm border border-[color:var(--con-border)]">
              <summary
                className={cn(
                  "flex cursor-pointer list-none items-center justify-between gap-2 px-2.5 py-1.5",
                  "text-[length:var(--con-text-xs)] text-[color:var(--con-on-surface-muted)]",
                  conFocusRing,
                )}
              >
                <span className="font-medium">
                  <Trans>Traccia di esecuzione</Trans>
                </span>
                <span className="font-mono text-[color:var(--con-on-surface-faint)] tabular-nums">
                  {(res.steps ?? []).length}
                </span>
              </summary>
              <ol className="flex flex-col gap-1 border-[color:var(--con-border)] border-t px-2.5 py-2">
                {(res.steps ?? []).map((step, idx) => (
                  <li
                    key={`${step.step}-${step.type}-${idx}`}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="flex min-w-0 items-center gap-1.5">
                      <span className="font-mono text-[length:var(--con-text-2xs)] text-[color:var(--con-on-surface-faint)] tabular-nums">
                        {step.step}
                      </span>
                      <span className="truncate font-mono text-[length:var(--con-text-2xs)] text-[color:var(--con-on-surface-muted)]">
                        {step.type}
                      </span>
                    </span>
                    <StepStatusBadge status={String(step.status)} />
                  </li>
                ))}
              </ol>
            </details>
          ) : null}

          {res.degraded ? (
            <p className="text-[length:var(--con-text-xs)] text-[color:var(--con-warning-text)]">
              <Trans>Risposta degradata.</Trans>
              {res.degraded_reason ? ` ${res.degraded_reason}` : null}
            </p>
          ) : null}
        </div>
      ) : (
        <p className="text-[length:var(--con-text-xs)] text-[color:var(--con-on-surface-faint)]">
          {i18n._(msg`Sto ragionando…`)}
        </p>
      )}
    </div>
  );
}

export function AssistPanel({
  open,
  onClose,
  contextLabel,
}: {
  open: boolean;
  onClose: () => void;
  /** The section the panel was opened from (context-aware eyebrow). */
  contextLabel: string;
}): React.ReactElement | null {
  const { i18n } = useLingui();
  const [input, setInput] = React.useState("");
  const [turns, setTurns] = React.useState<AssistTurn[]>([]);
  const conversationIdRef = React.useRef<string | null>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);

  const mutation = useMutation({
    mutationFn: async (
      question: string,
    ): Promise<{ question: string; res: ConsoleAgenticResponse }> => {
      let convId = conversationIdRef.current;
      if (!convId) {
        try {
          convId = (await createConsoleConversation(question)).conversation_id;
          conversationIdRef.current = convId;
        } catch {
          convId = null; // conversation store rejected — answer stateless.
        }
      }
      const body: ConsoleAgenticRequest = {
        query: question,
        k: 8,
        max_steps: 6,
        with_diagnostics: false,
        include_rows: false,
        ...(convId ? { conversation_id: convId } : {}),
      };
      const res = await runConsoleAgenticAnswer(body);
      return { question, res };
    },
    onSuccess: ({ question, res }) => {
      if (res.conversation_id) conversationIdRef.current = res.conversation_id;
      setTurns((prev) => [
        ...prev,
        { id: crypto.randomUUID(), question, response: res, error: null },
      ]);
    },
    onError: (err, question) => {
      setTurns((prev) => [
        ...prev,
        { id: crypto.randomUUID(), question, response: null, error: getConsoleErrorMessage(err) },
      ]);
    },
  });

  // Focus the prompt when the panel opens; scroll to the newest turn as it lands.
  React.useEffect(() => {
    if (open) textareaRef.current?.focus();
  }, [open]);
  React.useEffect(() => {
    const el = scrollRef.current;
    // ``scrollTo`` is unimplemented under jsdom — guard so the effect is inert there.
    if (el && typeof el.scrollTo === "function") {
      el.scrollTo({ top: el.scrollHeight });
    }
  }, [turns.length, mutation.isPending]);

  if (!open) return null;

  const submit = (): void => {
    const question = input.trim();
    if (question.length === 0 || mutation.isPending) return;
    setInput("");
    mutation.mutate(question);
  };

  const onKeyDown = (event: React.KeyboardEvent): void => {
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
      event.preventDefault();
      submit();
    }
  };

  return (
    <aside
      aria-label={i18n._(msg`Assistente della console`)}
      className={cn(
        "con-page-enter fixed top-12 right-0 bottom-0 z-[55] flex w-[min(380px,100vw)] flex-col",
        "border-[color:var(--con-border)] border-l bg-[color:var(--con-surface-raised)]",
        "shadow-[-12px_0_28px_-18px_rgba(31,29,26,0.4)]",
      )}
    >
      <header className="flex shrink-0 items-start justify-between gap-2 border-[color:var(--con-border)] border-b px-4 py-3">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 font-semibold text-[length:var(--con-text-2xs)] text-[color:var(--con-accent-text)] uppercase tracking-[0.08em]">
            <Sparkles size={12} strokeWidth={2} aria-hidden />
            <Trans>Assistente</Trans>
            <span className="text-[color:var(--con-on-surface-faint)]">· {contextLabel}</span>
          </p>
          <h2 className="mt-0.5 font-display font-semibold text-[length:var(--con-text-md)] text-[color:var(--con-on-surface)]">
            <Trans>Chiedi con parole tue</Trans>
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={i18n._(msg`Chiudi l'assistente`)}
          className={cn(
            "con-press shrink-0 rounded-sm p-1 text-[color:var(--con-on-surface-faint)]",
            "transition-colors duration-150 ease-out hover:text-[color:var(--con-on-surface)]",
            conFocusRing,
          )}
        >
          <X size={15} strokeWidth={1.75} aria-hidden />
        </button>
      </header>

      <div ref={scrollRef} className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-4">
        {turns.length === 0 && !mutation.isPending ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 px-2 text-center">
            <span
              aria-hidden
              className="flex size-9 items-center justify-center rounded-full bg-[color:var(--con-surface-sunken)] text-[color:var(--con-on-surface-faint)]"
            >
              <Sparkles size={16} strokeWidth={1.5} />
            </span>
            <p className="text-[length:var(--con-text-xs)] text-[color:var(--con-on-surface-muted)]">
              <Trans>
                Chiedi qualcosa sui tuoi dati o su come integrare Ando. Rispondo con lo stesso
                motore agentico del runtime, in sola lettura.
              </Trans>
            </p>
          </div>
        ) : null}
        {turns.map((turn) => (
          <TurnView key={turn.id} turn={turn} />
        ))}
        {mutation.isPending ? (
          <p className="text-[length:var(--con-text-xs)] text-[color:var(--con-on-surface-faint)] italic">
            <Trans>Sto ragionando…</Trans>
          </p>
        ) : null}
      </div>

      <div className="shrink-0 border-[color:var(--con-border)] border-t px-3 py-3">
        <ConTextarea
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          rows={3}
          placeholder={i18n._(msg`Chiedi qualcosa su ${contextLabel}…`)}
          aria-label={i18n._(msg`Prompt per l'assistente`)}
          className="min-h-16"
        />
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 text-[length:var(--con-text-2xs)] text-[color:var(--con-on-surface-faint)]">
            <ConKbd>
              <CornerDownLeft size={11} strokeWidth={1.75} aria-hidden />
            </ConKbd>
            <span>⌘</span>
            <Trans>per inviare</Trans>
          </span>
          <ConButton
            size="sm"
            disabled={input.trim().length === 0 || mutation.isPending}
            onClick={submit}
          >
            <Sparkles aria-hidden strokeWidth={2} />
            <Trans>Chiedi</Trans>
          </ConButton>
        </div>
      </div>
    </aside>
  );
}
